import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ReviewSection from '../components/ReviewSection';
import ProductCard from '../components/ProductCard';
import PriceDisplay from '../components/PriceDisplay';
import SEO from '../components/SEO';
import { ShoppingCart, Heart, Truck, Shield, RotateCcw, Star, Plus, Minus, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, getCartCount } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        
        // Fetch all products
        const response = await fetch(`${API_URL}/api/products`);
        const allProducts = await response.json();
        
        // Find product by id
        const foundProduct = allProducts.find(p => p.id === parseInt(id));
        
        if (foundProduct) {
          setProduct(foundProduct);
          
          // Get related products from same category
          const related = allProducts
            .filter(p => p.category === foundProduct.category && p.id !== foundProduct.id)
            .slice(0, 4);
          setRelatedProducts(related);
        } else {
          setProduct(null);
        }
      } catch (error) {
        console.error('Ürün yükleme hatası:', error);
        setProduct(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    
    // Log product visit for analytics (fire-and-forget, include auth if available)
    try {
      const token = localStorage.getItem('userToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      fetch(`${API_URL}/api/products/${id}/visit`, { method: 'POST', headers }).catch(() => {});
    } catch {}
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header cartCount={getCartCount()} />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Yükleniyor...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Header cartCount={getCartCount()} />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Ürün Bulunamadı</h1>
          <p className="text-gray-600 mb-6">Aradığınız ürün bulunamadı veya artık mevcut değil.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
          >
            Ana Sayfaya Dön
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Prepare images array - use all_images if available, otherwise fallback to image/image2
  const images = product.all_images && product.all_images.length > 0 
    ? product.all_images 
    : [product.image, product.image2, ...(product.images || [])].filter(Boolean);

  const handleAddToCart = () => {
    const MAX_QUANTITY_PER_PRODUCT = 3;
    
    if (product.stock === 0) {
      alert('Bu ürün stokta yok!');
      return;
    }
    if (quantity > product.stock) {
      alert(`Maksimum ${product.stock} adet ekleyebilirsiniz!`);
      return;
    }
    if (quantity > MAX_QUANTITY_PER_PRODUCT) {
      alert(`Her üründen maksimum ${MAX_QUANTITY_PER_PRODUCT} adet sipariş verebilirsiniz!`);
      return;
    }
    addToCart(product, quantity);
    alert(`${quantity} adet ${product.name} sepete eklendi!`);
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title={`${product.name} - Cep Premium`}
        description={product.description?.substring(0, 160) || `${product.name} ürününü inceleyip satın alın. Türkiye garantili, hızlı kargo.`}
        keywords={`${product.name}, cep premium, ${product.category}, teknoloji ürünleri`}
        image={product.image}
        type="product"
        productData={{
          name: product.name,
          description: product.description,
          image: product.image,
          price: product.price,
          stock: product.stock
        }}
      />
      <Header cartCount={getCartCount()} />

      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-600 hover:text-red-600">Ana Sayfa</Link>
            <span className="text-gray-400">/</span>
            <Link to={`/category/${product.category}`} className="text-gray-600 hover:text-red-600">
              {product.category}
            </Link>
            <span className="text-gray-400">/</span>
            <span className="text-gray-800">{product.name.substring(0, 40)}...</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Product Images */}
          <div>
            {/* Main Image */}
            <div className="relative bg-gray-50 rounded-lg overflow-hidden mb-4 group">
              <img
                src={images[selectedImage]}
                alt={product.name}
                className="w-full h-auto object-contain border-2 border-gray-300 shadow-sm"
                style={{ minHeight: '400px', maxHeight: '600px' }}
              />
              
              {/* Image Navigation */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}

              {/* Discount Badge */}
              {product.discount > 0 && (
                <div className="absolute top-4 left-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                  -{product.discount}%
                </div>
              )}
            </div>

            {/* Thumbnail Images */}
            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {images.slice(0, 4).map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`border-2 rounded-lg overflow-hidden ${
                      selectedImage === idx ? 'border-red-600' : 'border-gray-300'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-20 object-cover border border-gray-300 shadow-sm" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-4">{product.name}</h1>

            {/* Price */}
            <div className="mb-6">
              <PriceDisplay 
                price={product.price}
                oldPrice={product.old_price}
                discount={product.discount}
                size="large"
              />
            </div>

            {/* Stock Status */}
            <div className="mb-6">
              {product.stock > 0 ? (
                <span className="inline-flex items-center gap-2 text-green-600 font-medium">
                  <span className="w-2 h-2 bg-green-600 rounded-full"></span>
                  Stokta Var
                </span>
              ) : (
                <span className="inline-flex items-center gap-2 text-red-600 font-medium">
                  <span className="w-2 h-2 bg-red-600 rounded-full"></span>
                  Stokta Yok
                </span>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Miktar</label>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={quantity <= 1}
                >
                  <Minus size={16} />
                </button>
                <span className="w-16 text-center text-lg font-semibold">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(Math.min(product.stock, 3), quantity + 1))}
                  className="w-10 h-10 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={quantity >= Math.min(product.stock, 3)}
                >
                  <Plus size={16} />
                </button>
              </div>
              {quantity >= 3 && product.stock >= 3 && (
                <p className="text-xs text-orange-600 mt-2">
                  ⚠️ Her üründen maksimum 3 adet sipariş verebilirsiniz
                </p>
              )}
              {quantity >= product.stock && product.stock > 0 && product.stock < 3 && (
                <p className="text-xs text-orange-600 mt-2">
                  ⚠️ Maksimum stok miktarına ulaştınız ({product.stock} adet)
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mb-6">
              <button
                onClick={handleAddToCart}
                disabled={product.stock === 0}
                className="flex-1 bg-red-600 text-white py-4 rounded-lg font-semibold hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                <ShoppingCart size={20} />
                Sepete Ekle
              </button>
              <button
                onClick={() => setIsFavorite(!isFavorite)}
                className={`w-14 h-14 border-2 rounded-lg flex items-center justify-center transition-colors ${
                  isFavorite ? 'border-red-600 bg-red-50' : 'border-gray-300 hover:border-red-600'
                }`}
              >
                <Heart size={24} className={isFavorite ? 'fill-red-600 text-red-600' : 'text-gray-600'} />
              </button>
            </div>

            {/* Features */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t">
              <div className="text-center">
                <Truck size={32} className="mx-auto text-red-600 mb-2" />
                <p className="text-sm font-medium">Ücretsiz Kargo</p>
              </div>
              <div className="text-center">
                <Shield size={32} className="mx-auto text-red-600 mb-2" />
                <p className="text-sm font-medium">Güvenli Alışveriş</p>
              </div>
              <div className="text-center">
                <RotateCcw size={32} className="mx-auto text-red-600 mb-2" />
                <p className="text-sm font-medium">Kolay İade</p>
              </div>
            </div>
          </div>
        </div>

        {/* Product Description - Simple Layout */}
        {product.description && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Açıklama</h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                {product.description}
              </p>
            </div>
          </div>
        )}

        {/* Technical Specifications - Simple Layout (NO TABS) */}
        {product.technical_specs && product.technical_specs.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <span>⚙️</span> Teknik Özellikler
            </h2>
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {product.technical_specs.map((spec, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200"
                  >
                    <div className="flex-shrink-0 w-2 h-2 bg-red-600 rounded-full mt-2"></div>
                    <div className="flex-1">
                      <div className="font-semibold text-gray-800 text-sm">
                        {spec.key}
                      </div>
                      <div className="text-gray-600 text-sm mt-1">
                        {spec.value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Benzer Ürünler</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard
                  key={relatedProduct.id}
                  product={relatedProduct}
                  onAddToCart={() => {}}
                />
              ))}
            </div>
          </div>
        )}

        {/* Reviews */}
        <ReviewSection productId={product.id} />
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;
